'use client';
import {
  generateCartIdAction,
  getGooglePlaceDetails,
  getGoogleReverseGeocode,
  // getNearbyStores,
} from '@/app/actions';
import { endpoints, http, logger } from '@/config';
import {
  DEFAULT_COUNTRY,
  DEFAULT_STOCK_ID,
  DEFAULT_STORE_CODE,
  // STORE_REGION_ID_MAP,
  TOKEN_EXPIRY_TIME,
} from '@/constants';
import { storage } from '@/utils/storage';
import {
  FC,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
} from 'react';
import { useCleverTap } from './clevertapContext';
import LocationLoader from '@/features/Location/components/LocationLoader';
import { AddressUserDetails } from '@/models/user-details';
import { deleteCookie, setCookie } from 'cookies-next';
import { toast } from 'react-toastify';
import { isWebview } from '@/utils/isWebview';

interface LocationError {
  code: number;
  message: string;
}

interface StoreChangePropsType {
  store_code: string;
  stock_id: number | string;
  wizzy_api_key: string;
  wizzy_store_id: string;
  default_store_code: string;
}

interface CoordinatesPropTypes {
  lat: number;
  lng: number;
}

interface LocationToast {
  message: string;
  deliveryType?: 'home' | 'pickup';
}

interface LocationPropTypes {
  handleStoreChange: (
    updatedStoreDetails: StoreChangePropsType
  ) => Promise<boolean>;
  coordinates: CoordinatesPropTypes | null;
  setCoordinates: (coordinates: CoordinatesPropTypes) => void;
  showLocationModal: boolean;
  setShowLocationModal: (value: boolean) => void;
  defaultStore: string;
  placeId: string | null;
  setPlaceId: (placeId: string) => void;
  address: string | null;
  setAddress: (address: string) => void;
  locationToast?: LocationToast;
  setLocationToast: (value: LocationToast | undefined) => void;
  storeCode: string;
  setStoreCode: (storeCode: string) => void;
  expressStandard: boolean;
  expressStandardLoading: boolean;
  getMagentoStoreData: (position: {
    coords: {
      latitude: number;
      longitude: number;
      accuracy?: number;
      skipToast?: boolean;
      intentionalClick?: number;
      address?: string;
      id?: number;
      isAddressField?: boolean;
      isDeliveryPopup?: boolean;
      isCustomerAddressForm?: boolean;
    };
  }) => Promise<void>;
  handleSuggestionClick: (suggestionData: {
    prediction: any;
    address: string;
    onModalClose?: () => void;
    skipToast?: boolean;
    isDeliveryPopup?: boolean;
    isCustomerAddressForm?: boolean;
  }) => Promise<void>;
  selectedAddressId: string | null;
  setSelectedAddressId: (addressId: string) => void;
  showSearchManually: boolean;
  setShowSearchManually: (value: boolean) => void;
  addressString: string | null;
  setAddressString: (addressString: string) => void;
  city: string | null;
  setCity: (city: string) => void;
  state: string | null;
  setState: (state: string) => void;
  serviceable: boolean;
  setServiceable: (value: boolean) => void;
  showDeliveryModal: boolean;
  setShowDeliveryModal: (value: boolean) => void;
  emptyCart: boolean;
  setEmptyCart: (value: boolean) => void;
  pendingAddressChange: {
    prediction: any;
    lat?: number;
    lng?: number;
    data: any;
    id?: number;
  } | null;
  setPendingAddressChange: (
    change: {
      prediction: any;
      lat?: number;
      lng?: number;
      data: any;
      id?: number;
      isAddressField?: boolean;
    } | null
  ) => void;
  storeChangeError: boolean;
  setStoreChangeError: (value: boolean) => void;
  isCheckout: boolean;
  setIsCheckout: (value: boolean) => void;
  showPickupStore: boolean;
  setShowPickupStore: (value: boolean) => void;
  showNonServiceablePopup: boolean;
  setShowNonServiceablePopup: (value: boolean) => void;
  showStoreChangeModal: boolean;
  setShowStoreChangeModal: (value: boolean) => void;
  deliveryType: 'home' | 'pickup';
  setDeliveryType: (value: 'home' | 'pickup') => void;
  selectedStore: any;
  setSelectedStore: (value: any) => void;
  selectedPrediction: any;
  setSelectedPrediction: (value: any) => void;
  isDelivery: boolean;
  setIsDelivery: (value: boolean) => void;
  isClickedYes: boolean;
  setIsClickedYes: (value: boolean) => void;
  headerLocationTitle: string;
  setHeaderLocationTitle: (value: string) => void;
  isProcessingChange: boolean;
  setIsProcessingChange: (value: boolean) => void;
  isFetchingLocation: boolean;
  setIsFetchingLocation: (value: boolean) => void;
  geolocationOptions: {
    enableHighAccuracy: boolean;
    timeout: number;
    maximumAge: number;
  };
  showHomeDelivery: boolean;
  setShowHomeDelivery: (value: boolean) => void;
  billingAddress: any;
  setBillingAddress: (value: any) => void;
  isExpressDelivery: boolean;
  setIsExpressDelivery: (value: boolean) => void;
  defaultLat: number | null;
  setDefaultLat: (value: number | null) => void;
  defaultLng: number | null;
  setDefaultLng: (value: number | null) => void;
  addressSelected: boolean;
  setAddressSelected: (value: boolean) => void;
}

const Context = createContext<LocationPropTypes | null>(null);

const LocationProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const defaultStore = process.env.DEFAULT_STORE_CODE || 'vasantkunj';
  const { pushEvent } = useCleverTap();

  // Add a ref to track if we're already fetching location
  const isInitialLocationFetch = useRef(false);

  const [coordinates, setCoordinates] = useState<CoordinatesPropTypes | null>(
    null
  );
  const [placeId, setPlaceId] = useState<string | null>(null);
  const [address, setAddress] = useState<string | null>(null);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [storeCode, setStoreCode] = useState('');
  const [locationToast, setLocationToast] = useState<
    LocationToast | undefined
  >();
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(
    null
  );
  const [showHomeDelivery, setShowHomeDelivery] = useState(false);
  const [showSearchManually, setShowSearchManually] = useState(false);
  const [addressString, setAddressString] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [serviceable, setServiceable] = useState(false);
  const [deliveryType, setDeliveryType] = useState<'home' | 'pickup'>(
    storage.get('deliveryType') as 'home' | 'pickup'
  );
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [emptyCart, setEmptyCart] = useState(false);
  const [pendingAddressChange, setPendingAddressChange] = useState<{
    lng?: number;
    lat?: number;
    prediction: any;
    data: any;
    id?: number;
    isAddressField?: boolean;
  } | null>(null);
  const [storeChangeError, setStoreChangeError] = useState(false);
  const [showPickupStore, setShowPickupStore] = useState(false);
  const [showNonServiceablePopup, setShowNonServiceablePopup] = useState(false);
  const [showStoreChangeModal, setShowStoreChangeModal] = useState(false);
  const [selectedStore, setSelectedStore] = useState<any>(null);
  const [selectedPrediction, setSelectedPrediction] = useState<any>(null);
  const [isDelivery, setIsDelivery] = useState(false);
  const [isClickedYes, setIsClickedYes] = useState(false);
  const [isCheckout, setIsCheckout] = useState(false);
  const [headerLocationTitle, setHeaderLocationTitle] =
    useState('Fetching Location');
  const [isProcessingChange, setIsProcessingChange] = useState(false);
  const [isFetchingLocation, setIsFetchingLocation] = useState(false);
  const [billingAddress, setBillingAddress] =
    useState<AddressUserDetails | null>(null);
  const [isExpressDelivery, setIsExpressDelivery] = useState(false);
  const [defaultLat, setDefaultLat] = useState<number | null>(null);
  const [defaultLng, setDefaultLng] = useState<number | null>(null);
  const [addressSelected, setAddressSelected] = useState(false);
  const [expressStandard, setExpressStandard] = useState(false);
  const [expressStandardLoading, setExpressStandardLoading] = useState(false);

  const lat = storage.get('lat');
  const lng = storage.get('lng');

  useEffect(() => {
    fetchDeliveryMethods();
  }, [lat, lng]);

  const fetchDeliveryMethods = async () => {
    setExpressStandardLoading(true);
    try {
      const url = `${endpoints.EXPRESS_STANDARD_CHECK_SERVICEABILITY}?latitude=${lat}&longitude=${lng}`;
      const {
        response,
        data,
      }: { response: any; data: { express_standard_delivery: boolean } } =
        await http.get(url);
      if (response.ok) {
        setExpressStandard(data.express_standard_delivery);
      }
    } catch (error) {
      logger.error('Error in fetchDeliveryMethods:', error);
    } finally {
      setExpressStandardLoading(false);
    }
  };
  // const regionId =
  //   searchParams.get('region_id') &&
  //   (searchParams.get('region_id') as keyof typeof STORE_REGION_ID_MAP);

  const geolocationOptions = {
    enableHighAccuracy: true,
    timeout: 6000,
    maximumAge: 0,
  };

  const handleLocationError = (error: any, context: string): LocationError => {
    logger.error(`Error in ${context}:`, error);
    return {
      code: error?.code || 500,
      message: error?.message || 'An unexpected error occurred',
    };
  };

  const handleStoreChange = useCallback(
    async (updatedStoreDetails: StoreChangePropsType) => {
      try {
        // Validate required fields
        if (
          !updatedStoreDetails?.store_code ||
          !updatedStoreDetails?.stock_id
        ) {
          throw new Error('Missing required store details');
        }

        // Remove Old Cart Data
        storage.remove('cart_id');
        storage.remove('quote_id');
        deleteCookie('quote_id');
        deleteCookie('cart_id');

        // Set New Store Data with validation
        const storeData = {
          default_store_code:
            updatedStoreDetails?.default_store_code || DEFAULT_STORE_CODE,
          store_code: updatedStoreDetails?.store_code,
          stock_id: String(updatedStoreDetails?.stock_id),
          wizzy_api_key: updatedStoreDetails?.wizzy_api_key,
          wizzy_store_id: updatedStoreDetails?.wizzy_store_id,
        };

        // Validate store data before setting
        Object.entries(storeData).forEach(([key, value]) => {
          if (value) {
            storage.set(key, value);
          }
        });

        const cookiesToSet = [
          { key: 'default_store_code', value: storeData.default_store_code },
          { key: 'store_code', value: storeData.store_code },
          { key: 'stock_id', value: storeData.stock_id },
          { key: 'wizzy_api_key', value: storeData.wizzy_api_key },
          { key: 'wizzy_store_id', value: storeData.wizzy_store_id },
        ];

        for (const cookie of cookiesToSet) {
          setCookie(cookie.key, cookie.value, {
            maxAge: TOKEN_EXPIRY_TIME,
          });
        }

        // Generate New Cart Details with validation
        const cartKeysString = await generateCartIdAction();
        if (!cartKeysString) {
          throw new Error('Failed to generate new cart');
        }

        const cartKeys = JSON.parse(cartKeysString);
        if (!cartKeys?.key || !cartKeys?.value) {
          throw new Error('Invalid cart keys format');
        }

        storage.set(cartKeys.key, cartKeys.value);
        return true;
      } catch (e: any) {
        const error = handleLocationError(e, 'handleStoreChange');
        logger.error('Error while updating store keys:', error);
        return false;
      }
    },
    []
  );

  const handleSuggestionClick = useCallback(
    async ({
      prediction,
      address,
      onModalClose,
      skipToast = false,
      isDeliveryPopup = false,
      isCustomerAddressForm = false,
    }: {
      prediction: any;
      address: string;
      onModalClose?: () => void;
      skipToast?: boolean;
      isDeliveryPopup?: boolean;
      isCustomerAddressForm?: boolean;
    }) => {
      try {
        setSelectedPrediction(prediction);
        storage.set('prediction', JSON.stringify(prediction));
        setSelectedStore(null);
        const oldStore = storage.get('store_code');
        storage.set('selectedAddressId', '');
        setSelectedAddressId('');
        const dataString = await getGooglePlaceDetails(prediction.place_id);
        if (dataString) {
          const { data } = JSON.parse(dataString);

          if (!isCustomerAddressForm) {
            // Store latest postcode directly for later checkout usage
            const postcode = data?.result?.[0]?.pincode;
            if (postcode) {
              try {
                storage.set('pincode', String(postcode));
              } catch (error) {
                logger.error(
                  'Error saving pincode in storage from suggestion:',
                  error
                );
              }
            }
            if (data?.properties[0]?.express_delivery === 1) {
              setIsExpressDelivery(true);
              storage.set('isExpressDelivery', 'true');
            } else {
              setIsExpressDelivery(false);
              storage.set('isExpressDelivery', 'false');
            }
            if (data?.store_code !== '') {
              const cookiesToSet = [
                { key: 'default_store_code', value: data?.default_store_code },
                {
                  key: 'store_code',
                  value:
                    data?.store_code ||
                    data?.default_store_code ||
                    DEFAULT_STORE_CODE,
                },
                {
                  key: 'serviceable',
                  value: data?.store_code === '' ? 'false' : 'true',
                },
                { key: 'lat', value: String(data?.result[0]?.lat) },
                { key: 'lng', value: String(data?.result[0]?.lng) },
                { key: 'wizzy_api_key', value: data?.wizzy_api_key },
                { key: 'wizzy_store_id', value: data?.wizzy_store_id },
                { key: 'stock_id', value: data?.stock_id || DEFAULT_STOCK_ID },
                { key: 'placeId', value: placeId },
              ];

              // Set cookies with maxAge where applicable
              for (const cookie of cookiesToSet) {
                setCookie(cookie.key, cookie.value, {
                  maxAge: TOKEN_EXPIRY_TIME,
                });
              }
            }
            const checkStoreValue =
              data?.store_code || data?.default_store_code;
            if (checkStoreValue !== oldStore) {
              const newCartKeys = await generateCartIdAction();
              if (newCartKeys) {
                const parsedCartKeys = JSON.parse(newCartKeys);
                if (parsedCartKeys) {
                  storage.set(parsedCartKeys?.key, parsedCartKeys?.value);
                }
              }
            }
            const addressParts = address
              .split(',')
              .map((part: string) => part.trim());
            const cityPart = addressParts[addressParts.length - 3]; // Usually city is third from last
            const statePart = addressParts[addressParts.length - 2]; // State is usually second from last
            setCity(cityPart);
            setState(statePart);
            if (data?.store_code === '') {
              storage.set('serviceable', 'false');
              setAddress(address);
              setServiceable(false);
            } else {
              storage.set('serviceable', 'true');
              setPlaceId(placeId);
              setAddress(address);
              setServiceable(true);
              storage.set(
                'address',
                prediction.structured_formatting?.main_text
              );
              setHeaderLocationTitle(
                prediction.structured_formatting?.main_text
              );
              storage.set(
                'addressTitle',
                prediction.structured_formatting?.main_text
              );
              storage.set(
                'addressDescription',
                prediction.structured_formatting?.secondary_text
              );
              storage.set('lat', data?.result[0]?.lat);
              storage.set('lng', data?.result[0]?.lng);
              if (data?.result?.[0]?.lat && data?.result?.[0]?.lng) {
                (window as any).clevertap?.getLocation(
                  Number(data.result[0].lat),
                  Number(data.result[0].lng)
                );
              }
              storage.set('placeId', prediction.place_id);
              storage.set('wizzy_api_key', data?.wizzy_api_key);
              storage.set('wizzy_store_id', data?.wizzy_store_id);
              storage.set('deliveryType', 'home');
              setDeliveryType('home');
              storage.set(
                'default_store_code',
                data?.default_store_code || DEFAULT_STORE_CODE
              );
              storage.set('store_code', data?.store_code || DEFAULT_STORE_CODE);
              setStoreCode(data?.store_code || DEFAULT_STORE_CODE);
              storage.set(
                'home_store_code',
                data?.store_code || DEFAULT_STORE_CODE
              );
              storage.set('stock_id', data?.stock_id || DEFAULT_STOCK_ID);
              storage.set('city', cityPart);
              storage.set('state', statePart);
            }
            const ctCity = data?.result?.[0]?.city || cityPart;
            const ctState = data?.result?.[0]?.state || statePart;
            (window as any).clevertap?.profile?.push({
              Site: {
                ...(ctCity && { City: ctCity }),
                ...(ctState && { Region: ctState }),
                Country: DEFAULT_COUNTRY,
              },
            });
            setSelectedPrediction(prediction);
            storage.set('prediction', JSON.stringify(prediction));
            setStoreChangeError(false);
            storage.set('store_change_error', 'false');
            storage.set('addressString', address);
            setAddressString(address);
            setDefaultLat(data?.result[0]?.lat);
            setDefaultLng(data?.result[0]?.lng);
            const serviceable = storage.get('serviceable');
            if (onModalClose) onModalClose();
            if (!skipToast) {
              if (serviceable === 'true') {
                if (!isCheckout && serviceable === 'true' && !isDeliveryPopup) {
                  storage.set('show_location_toast', 'true');
                  window.location.reload();
                } else {
                  setLocationToast({
                    message:
                      "You're in our service area! Delivery typically takes 3-4 hours",
                    deliveryType: 'home',
                  });
                  setTimeout(() => setLocationToast(undefined), 2000);
                }
              }
            } else if (
              !isCheckout &&
              serviceable === 'true' &&
              !isDeliveryPopup
            ) {
              window.location.reload();
            }
          } else {
            storage.set('customer_addressString', address);
            if (data?.store_code === '') {
              storage.set('customer_serviceable', 'false');
              setServiceable(false);
            } else {
              storage.set('customer_serviceable', 'true');
              storage.set('customer_lat', data?.result[0]?.lat);
              storage.set('customer_lng', data?.result[0]?.lng);
              setServiceable(true);
            }
          }
        } else {
          throw Error('Failed to update location. Please try again.');
        }
      } catch (error) {
        logger.error('Error fetching place details:', error);
      }
    },
    []
  );

  const getMagentoStoreData = useCallback(
    async (position: {
      coords: {
        latitude: number;
        longitude: number;
        accuracy?: number;
        skipToast?: boolean;
        intentionalClick?: number;
        address?: string;
        id?: number;
        isAddressField?: boolean;
        isDeliveryPopup?: boolean;
        isCustomerAddressForm?: boolean;
      };
    }) => {
      try {
        // Validate position data
        if (!position?.coords?.latitude || !position?.coords?.longitude) {
          throw new Error('Invalid position coordinates');
        }

        setSelectedStore(null);
        const {
          latitude,
          longitude,
          accuracy,
          intentionalClick = 1,
          skipToast = false,
          address,
          id,
          isAddressField = false,
          isDeliveryPopup = false,
          isCustomerAddressForm = false,
        } = position.coords;

        // Validate coordinates
        if (isNaN(latitude) || isNaN(longitude)) {
          throw new Error('Invalid coordinate values');
        }

        const oldStoreCode = storage.get('store_code');
        const isDelivery = storage.get('isDelivery');
        const dataString = await getGoogleReverseGeocode(
          latitude,
          longitude,
          intentionalClick
        );

        if (!dataString) {
          throw new Error('Failed to get reverse geocode data');
        }

        const { data } = JSON.parse(dataString);
        const postcode = data?.result[0]?.pincode;
        const storeCode =
          data?.store_code || data?.default_store_code || DEFAULT_STORE_CODE;

        if (isCustomerAddressForm) {
          const addressString =
            data?.result[0].title + ',' + data?.result[0].sub_title;
          storage.set('customer_addressString', addressString);
          if (data?.store_code === '') {
            storage.set('customer_serviceable', 'false');
            setServiceable(false);
          } else {
            storage.set('customer_serviceable', 'true');
            storage.set('customer_lat', String(latitude));
            storage.set('customer_lng', String(longitude));
            (window as any).clevertap?.getLocation(latitude, longitude);
            setServiceable(true);
          }
        } else {
          // Store latest postcode directly for later checkout usage
          if (postcode) {
            try {
              storage.set('pincode', String(postcode));
            } catch (error) {
              logger.error(
                'Error saving pincode in storage from reverse geocode:',
                error
              );
            }
          }
          if (data?.properties[0]?.express_delivery === 1) {
            setIsExpressDelivery(true);
            storage.set('isExpressDelivery', 'true');
          } else {
            setIsExpressDelivery(false);
            storage.set('isExpressDelivery', 'false');
          }
          if (data?.store_code !== '') {
            const cookiesToSet = [
              { key: 'default_store_code', value: data?.default_store_code },
              { key: 'store_code', value: storeCode },
              {
                key: 'serviceable',
                value: data?.store_code === '' ? 'false' : 'true',
              },
              { key: 'lat', value: String(latitude) },
              { key: 'lng', value: String(longitude) },
              { key: 'wizzy_api_key', value: data?.wizzy_api_key },
              { key: 'wizzy_store_id', value: data?.wizzy_store_id },
              { key: 'stock_id', value: data?.stock_id || DEFAULT_STOCK_ID },
            ];
            for (const cookie of cookiesToSet) {
              setCookie(cookie.key, cookie.value, {
                maxAge: TOKEN_EXPIRY_TIME,
              });
            }
            (window as any).clevertap?.getLocation(latitude, longitude);
          }
          const ctCity = data?.result?.[0]?.city;
          const ctState = data?.result?.[0]?.state;
          (window as any).clevertap?.profile?.push({
            Site: {
              ...(ctCity && { City: ctCity }),
              ...(ctState && { Region: ctState }),
              Country: DEFAULT_COUNTRY,
            },
          });
          const newStoreCode = data?.store_code || data?.default_store_code;
          if (newStoreCode !== oldStoreCode) {
            const newCartKeys = await generateCartIdAction();
            if (newCartKeys) {
              const parsedCartKeys = JSON.parse(newCartKeys);
              if (parsedCartKeys) {
                storage.set(parsedCartKeys?.key, parsedCartKeys?.value);
              }
            }
          }
          setCoordinates({ lat: latitude, lng: longitude });
          const addressString =
            data?.result[0].title + ',' + data?.result[0].sub_title;
          if (data?.store_code === '') {
            storage.set('serviceable', 'false');
            setServiceable(false);
            setAddress(addressString);
            storage.set('addressString', addressString);
            setAddressString(addressString);
          } else {
            storage.set('serviceable', 'true');
            setServiceable(true);
            if (address) {
              setAddress(address);
              storage.set('selectedAddressId', id?.toString() || '');
              const addressPart = address.split(',')[0];
              storage.set('address', addressPart);
              setHeaderLocationTitle(addressPart);
              storage.set('addressTitle', addressPart);
              storage.set('addressDescription', address);
              storage.set('addressString', address);
              setAddressString(addressString);
            } else {
              setAddress(addressString);
              storage.set('address', data?.result[0]?.title);
              setHeaderLocationTitle(data?.result[0]?.title);
              storage.set('addressTitle', data?.result[0]?.title);
              storage.set('addressDescription', data?.result[0]?.sub_title);
              storage.set('addressString', addressString);
              setAddressString(addressString);
              storage.set('selectedAddressId', '');
            }
            storage.set('lat', String(latitude));
            storage.set('lng', String(longitude));
            storage.set('placeId', '');
            if (accuracy) {
              storage.set('accuracy', String(accuracy));
            }
            setStoreCode(storeCode);
            storage.set('default_store_code', data?.default_store_code);
            storage.set('store_code', data?.store_code);
            storage.set('stock_id', String(data?.stock_id));
            storage.set('wizzy_api_key', data?.wizzy_api_key);
            storage.set('wizzy_store_id', data?.wizzy_store_id);
            storage.set('home_store_code', storeCode);
            storage.set('city', data?.result[0]?.city);
            storage.set('state', data?.result[0]?.state);
          }
          storage.set('deliveryType', 'home');
          setDeliveryType('home');
          setStoreChangeError(false);
          storage.set('store_change_error', 'false');
          setAddressString(addressString);
          setDefaultLat(latitude);
          setDefaultLng(longitude);
          const serviceable = storage.get('serviceable');
          if (
            isAddressField === false &&
            storage.get('serviceable') === 'false' &&
            !isDeliveryPopup &&
            !isDelivery
          ) {
            setShowNonServiceablePopup(true);
          }
          if (!skipToast) {
            if (serviceable === 'true') {
              if (!isCheckout && serviceable === 'true' && !isDeliveryPopup) {
                storage.set('show_location_toast', 'true');
                window.location.reload();
              } else {
                setLocationToast({
                  message:
                    "You're in our service area! Delivery typically takes 3-4 hours",
                  deliveryType: 'home',
                });
                setTimeout(() => setLocationToast(undefined), 2000);
              }
            }
          } else if (
            !isCheckout &&
            serviceable === 'true' &&
            !isDeliveryPopup
          ) {
            window.location.reload();
          }
        }
      } catch (error: any) {
        const locationError = handleLocationError(error, 'getMagentoStoreData');
        logger.error('Error fetching Magento store data:', locationError);
        throw locationError;
      }
    },
    []
  );

  const checkLocation = useCallback(async () => {
    // If we're already fetching location, don't start another fetch
    if (isInitialLocationFetch.current) {
      return;
    }
    // Prevent LocationFlow if SearchManually is open
    if (showSearchManually) {
      return;
    }
    const storedLat = storage.get('lat');
    const storedLng = storage.get('lng');
    const storedPlaceId = storage.get('placeId');
    const storedAddress = storage.get('addressString');
    const storedSelectedAddressId = storage.get('selectedAddressId');
    const deliveryType = storage.get('deliveryType');
    const isDelivery = storage.get('isDelivery');

    // Show location modal if not serviceable or no address
    if (
      storage.get('serviceable') === 'false' &&
      !isDelivery &&
      deliveryType === 'home'
    ) {
      setShowNonServiceablePopup(true);
      return;
    }

    if (storedLat && storedLng) {
      setCoordinates({
        lat: parseFloat(storedLat),
        lng: parseFloat(storedLng),
      });
    } else if (storedPlaceId) {
      setPlaceId(storedPlaceId);
    } else if (storedSelectedAddressId) {
      setSelectedAddressId(storedSelectedAddressId);
    } else if (storedAddress) {
      setAddress(storedAddress);
    } else {
      if (navigator?.geolocation) {
        // Set flag before starting location request
        isInitialLocationFetch.current = true;

        const handleSuccess = async (position: GeolocationPosition) => {
          try {
            await getMagentoStoreData(position);
            pushEvent({
              event: 'GPS Location Fetched',
              data: {
                'Page URL': window.location.href,
                'Page Name': document.title,
                Serviceable: storage.get('serviceable') || false,
                'Store Code': storage.get('store_code') || DEFAULT_STORE_CODE,
                'Prompt Source': 'Initial',
              },
            });
          } catch (error) {
            logger.error('Error in handleSuccess:', error);
          } finally {
            setIsFetchingLocation(false);
            setShowLocationModal(false);
            isInitialLocationFetch.current = false;
          }
        };

        const handleError = (error: GeolocationPositionError) => {
          setIsFetchingLocation(false);
          isInitialLocationFetch.current = false;
          logger.error('Geolocation error:', error);
          const isSafari = /^((?!chrome|android).)*safari/i.test(
            navigator?.userAgent
          );
          if (
            (isSafari ||
              (error.code === error.PERMISSION_DENIED &&
                !storage.get('addressString') &&
                !storage.get('address'))) &&
            !showSearchManually &&
            !addressSelected // Prevent LocationFlow if SearchManually is open
          ) {
            setShowLocationModal(true);
          }
          pushEvent({
            event: 'GPS Location Permission Query',
            data: {
              'Page URL': window.location.href,
              'Page Name': document.title,
              Permission: 'Denied',
              'Prompt Source': 'Initial',
            },
          });
        };

        const handleErrorPermissionChange = (
          error: GeolocationPositionError
        ) => {
          setIsFetchingLocation(false);
          logger.error('Geolocation error:', error);
          const isSafari = /^((?!chrome|android).)*safari/i.test(
            navigator?.userAgent
          );
          if (
            (isSafari ||
              (error.code === error.PERMISSION_DENIED &&
                !storage.get('addressString') &&
                !storage.get('address'))) &&
            !showSearchManually &&
            !addressSelected // Prevent LocationFlow if SearchManually is open
          ) {
            setShowLocationModal(true);
          }
        };

        try {
          // Enhanced browser detection
          const userAgent = navigator?.userAgent?.toLowerCase();
          const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
          const isChrome = /chrome/i.test(userAgent) && !/edg/i.test(userAgent);
          const isFacebookBrowser = /fbios|fban|fbav/i.test(userAgent);
          const isInstagramBrowser = /instagram/i.test(userAgent);
          const isAndroid =
            /android/i.test(userAgent) ||
            /linux.*android|android.*linux/i.test(userAgent) ||
            /Android/i.test(window?.navigator?.userAgent);

          // For Android devices, directly request location permission
          if (isAndroid) {
            navigator?.geolocation?.getCurrentPosition(
              handleSuccess,
              handleError,
              geolocationOptions
            );
            return;
          }

          // Show location modal directly for in-app browsers and others except Safari and Chrome
          if (
            ((!isSafari && !isChrome) ||
              isFacebookBrowser ||
              isInstagramBrowser) &&
            !showSearchManually &&
            !addressSelected // Prevent LocationFlow if SearchManually is open
          ) {
            setShowLocationModal(true);
            isInitialLocationFetch.current = false;
            return;
          }

          if (isSafari) {
            navigator?.geolocation?.getCurrentPosition(
              position => {
                // Show loader immediately when user clicks Allow on Safari
                setIsFetchingLocation(true);
                handleSuccess(position);
              },
              handleError,
              geolocationOptions
            );
          } else {
            if (navigator?.permissions && navigator?.permissions?.query) {
              navigator?.permissions
                .query({ name: 'geolocation' })
                .then(permissionStatus => {
                  const handlePermissionChange = () => {
                    const eventData = {
                      'Page URL': window.location.href,
                      'Page Name': document.title,
                    };

                    switch (permissionStatus?.state) {
                      case 'granted':
                        // Show loader immediately when permission is granted
                        setIsFetchingLocation(true);
                        pushEvent({
                          event: 'GPS Location Permission Query',
                          data: {
                            'Page URL': window.location.href,
                            'Page Name': document.title,
                            Permission: 'Granted',
                            'Store Code': storage.get('store_code'),
                            'Prompt Source': 'Initial',
                          },
                        });
                        navigator?.geolocation?.getCurrentPosition(
                          handleSuccess,
                          handleError,
                          geolocationOptions
                        );
                        break;
                      case 'denied':
                        pushEvent({
                          event: 'GPS Location Permission Query',
                          data: {
                            'Page URL': window.location.href,
                            'Page Name': document.title,
                            Permission: 'Denied',
                            'Store Code': storage.get('store_code'),
                            'Prompt Source': 'Initial',
                          },
                        });
                        isInitialLocationFetch.current = false;
                        if (!showSearchManually && !addressSelected)
                          setShowLocationModal(true);
                        break;
                      case 'prompt':
                        pushEvent({
                          event: 'GPS Location Permission Query',
                          data: {
                            'Page URL': window.location.href,
                            'Page Name': document.title,
                            Permission: 'Not Set',
                            'Store Code': storage.get('store_code'),
                            'Prompt Source': 'Initial',
                          },
                        });
                        pushEvent({
                          event: 'GPS Native Prompt Shown',
                          data: {
                            ...eventData,
                            'Prompt Source': 'Initial',
                          },
                        });
                        navigator?.geolocation?.getCurrentPosition(
                          position => {
                            // Show loader immediately when user clicks Allow
                            setIsFetchingLocation(true);
                            handleSuccess(position);
                          },
                          handleErrorPermissionChange,
                          geolocationOptions
                        );
                        break;
                    }
                  };

                  // Only add the event listener once
                  if (!permissionStatus.onchange) {
                    permissionStatus.addEventListener(
                      'change',
                      handlePermissionChange
                    );
                  }
                  handlePermissionChange();

                  return () => {
                    permissionStatus.removeEventListener(
                      'change',
                      handlePermissionChange
                    );
                  };
                })
                .catch(() => {
                  navigator?.geolocation?.getCurrentPosition(
                    position => {
                      // Show loader immediately when user clicks Allow
                      setIsFetchingLocation(true);
                      handleSuccess(position);
                    },
                    handleError,
                    geolocationOptions
                  );
                });
            } else {
              pushEvent({
                event: 'GPS Native Prompt Shown',
                data: {
                  'Page URL': window.location.href,
                  'Page Name': document.title,
                  'Prompt Source': 'Initial',
                },
              });
              navigator?.geolocation?.getCurrentPosition(
                position => {
                  // Show loader immediately when user clicks Allow
                  setIsFetchingLocation(true);
                  handleSuccess(position);
                },
                handleError,
                geolocationOptions
              );
            }
          }
        } catch (error) {
          logger.error('Geolocation request failed:', error);
          pushEvent({
            event: 'Geolocation request failed',
            data: {
              Error: error,
            },
          });
          setIsFetchingLocation(false);
          // Reset the flag on error
          isInitialLocationFetch.current = false;
          if (!showSearchManually && !addressSelected)
            setShowLocationModal(true);
        }
      } else {
        if (!showSearchManually && !addressSelected) setShowLocationModal(true);
      }
    }
  }, [
    getMagentoStoreData,
    pushEvent,
    setAddress,
    setCoordinates,
    setPlaceId,
    setSelectedAddressId,
    showSearchManually,
  ]);

  const handlePendingAddressChange = useCallback(async () => {
    if (emptyCart && pendingAddressChange && isClickedYes) {
      try {
        const selectedAddress = pendingAddressChange.prediction.description;

        // Update address in storage and state
        storage.set('addressString', selectedAddress);
        setAddress(selectedAddress);

        let cityPart = '';
        let statePart = '';

        // Parse and store address components
        if (
          pendingAddressChange.data.result?.[0]?.city &&
          pendingAddressChange.data.result?.[0]?.state
        ) {
          cityPart = pendingAddressChange.data.result?.[0]?.city;
          statePart = pendingAddressChange.data.result?.[0]?.state;
        } else {
          const addressParts = selectedAddress
            .split(',')
            .map((part: string) => part.trim());
          cityPart = addressParts[addressParts.length - 3];
          statePart = addressParts[addressParts.length - 2];
        }

        storage.set('city', cityPart);
        storage.set('state', statePart);

        // Handle serviceability check
        if (pendingAddressChange.prediction.place_id) {
          await handleSuggestionClick({
            prediction: pendingAddressChange.prediction,
            address: selectedAddress,
            skipToast: false,
          });
        } else {
          await getMagentoStoreData({
            coords: {
              latitude: pendingAddressChange?.lat || 0,
              longitude: pendingAddressChange?.lng || 0,
              intentionalClick: 1,
              address: pendingAddressChange?.prediction?.description,
              id: pendingAddressChange?.id,
              isAddressField: pendingAddressChange?.isAddressField,
              skipToast: false,
            },
          });
        }

        // Clear states
        setPendingAddressChange(null);
        setEmptyCart(false);

        // Handle navigation after store change is complete
        if (isCheckout) {
          // Set a flag to indicate store change is complete
          storage.set('store_change_complete', 'true');
          // Use setTimeout to ensure state updates are complete before navigation
          setTimeout(() => {
            window.location.href = '/';
          }, 100);
        }
      } catch (error) {
        logger.error('Error processing address change:', error);
        setPendingAddressChange(null);
        setEmptyCart(false);
      }
    }
  }, [emptyCart, pendingAddressChange, isClickedYes, isCheckout]);

  const generateCartId = async () => {
    const data = await generateCartIdAction();
    if (data) {
      const parsedData = JSON.parse(data);
      if (parsedData?.value) {
        storage.set(parsedData?.key, parsedData?.value);
        return { key: parsedData?.key, value: parsedData?.value };
      }
      toast.error('Error Occurred while generating cart Id');
    }
    toast.error('Error Occurred while generating cart Id');
  };

  const checkVasantKunjServiceability = useCallback(async () => {
    const lat = storage.get('lat') ? Number(storage.get('lat')) : undefined;
    const lng = storage.get('lng') ? Number(storage.get('lng')) : undefined;
    if (lat === undefined || lng === undefined) {
      logger.error('Latitude or Longitude is missing for Vasant Kunj check');
      return;
    }
    const dataString = (await getGoogleReverseGeocode(lat, lng)) ?? [];
    if (dataString && typeof dataString === 'string') {
      const { data } = JSON.parse(dataString);
      if (data?.store_code !== 'vasantkunj') {
        if (data?.store_code === '') {
          storage.set('serviceable', 'false');
          storage.set('checked_vasantkunj', 'true');
          return;
        }
        const keyValuePairs = [
          { key: 'serviceable', value: 'true' },
          {
            key: 'default_store_code',
            value: data?.default_store_code || DEFAULT_STORE_CODE,
          },
          { key: 'store_code', value: data?.store_code },
          { key: 'stock_id', value: data?.stock_id || DEFAULT_STOCK_ID },
          { key: 'wizzy_api_key', value: data?.wizzy_api_key },
          { key: 'wizzy_store_id', value: data?.wizzy_store_id },
        ];
        for (const { key, value } of keyValuePairs) {
          storage.set(key, value);
          setCookie(key, value, {
            maxAge: TOKEN_EXPIRY_TIME,
          });
        }
        storage.set('checked_vasantkunj', 'true');
        setStoreCode(data?.store_code);
        setServiceable(true);
        await generateCartId();
        setTimeout(() => window.location.reload(), 500);
      }
    }
    storage.set('checked_vasantkunj', 'true');
  }, []);

  useEffect(() => {
    const store_code = storage.get('store_code');
    const hasCheckedVasantKunj = storage.get('checked_vasantkunj');
    const isHomeDelivery = storage.get('deliveryType') === 'home';
    if (
      store_code === 'vasantkunj' &&
      !hasCheckedVasantKunj &&
      isHomeDelivery
    ) {
      checkVasantKunjServiceability();
    }
  }, []);

  useEffect(() => {
    const deliveryType =
      (storage.get('deliveryType') as 'home' | 'pickup') || 'pickup';
    const headerLocationTitleString = storage.get('address');
    if (deliveryType) {
      setDeliveryType(deliveryType);
    }
    if (headerLocationTitleString) {
      setHeaderLocationTitle(headerLocationTitleString);
    }
  }, []);

  useEffect(() => {
    if (!isWebview()) {
      checkLocation();
    }
  }, [checkLocation]);

  useEffect(() => {
    handlePendingAddressChange();
  }, [emptyCart, pendingAddressChange, isClickedYes]);

  // Add useEffect to check for pending toast on mount
  useEffect(() => {
    const shouldShowToast = storage.get('show_location_toast');
    if (shouldShowToast === 'true') {
      storage.remove('show_location_toast');
      if (storage.get('deliveryType') === 'home') {
        setLocationToast({
          message:
            "You're in our service area! Delivery typically takes 3-4 hours",
          deliveryType: 'home',
        });
      } else {
        setLocationToast({
          message: 'Your Pickup Store has been changed.',
          deliveryType: 'pickup',
        });
      }
      setTimeout(() => setLocationToast(undefined), 2000);
    }
  }, []);

  const value: LocationPropTypes = {
    handleStoreChange,
    coordinates,
    setCoordinates,
    showLocationModal,
    setShowLocationModal,
    defaultStore,
    placeId,
    setPlaceId,
    address,
    setAddress,
    locationToast,
    setLocationToast,
    storeCode,
    setStoreCode,
    getMagentoStoreData,
    handleSuggestionClick,
    selectedAddressId,
    setSelectedAddressId,
    showSearchManually,
    setShowSearchManually,
    addressString,
    setAddressString,
    city,
    setCity,
    state,
    setState,
    serviceable,
    setServiceable,
    showDeliveryModal,
    setShowDeliveryModal,
    emptyCart,
    setEmptyCart,
    pendingAddressChange,
    setPendingAddressChange,
    storeChangeError,
    setStoreChangeError,
    isCheckout,
    setIsCheckout,
    showPickupStore,
    setShowPickupStore,
    showNonServiceablePopup,
    setShowNonServiceablePopup,
    showStoreChangeModal,
    setShowStoreChangeModal,
    deliveryType,
    setDeliveryType,
    selectedStore,
    setSelectedStore,
    selectedPrediction,
    setSelectedPrediction,
    isDelivery,
    setIsDelivery,
    isClickedYes,
    setIsClickedYes,
    headerLocationTitle,
    setHeaderLocationTitle,
    isProcessingChange,
    setIsProcessingChange,
    isFetchingLocation,
    setIsFetchingLocation,
    geolocationOptions,
    showHomeDelivery,
    setShowHomeDelivery,
    billingAddress,
    setBillingAddress,
    isExpressDelivery,
    setIsExpressDelivery,
    defaultLat,
    setDefaultLat,
    defaultLng,
    setDefaultLng,
    addressSelected,
    setAddressSelected,
    expressStandard,
    expressStandardLoading,
  };

  return (
    <Context.Provider value={value}>
      <LocationLoader show={isFetchingLocation} />
      {children}
    </Context.Provider>
  );
};

export const useLocation = (): LocationPropTypes => {
  const context = useContext(Context);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};

export default LocationProvider;
